/*
 * Reading what the backend actually said.
 *
 * Every checkout handler in this app used to do this:
 *
 *     alert(error?.Message || error?.message || 'There was an error …')
 *
 * `error` there is the AxiosError, not the response body. `AxiosError.Message`
 * does not exist and `AxiosError.message` is always set to axios's own string,
 * so the guest read "Request failed with status code 409" and the friendly
 * fallback on the third line was unreachable for every HTTP error.
 *
 * The backend answers 409 with a real explanation — the venue is closed, the
 * category is not served now, an item is sold out — and none of it was ever
 * shown. These helpers put the body first.
 */

/*
 * The server's own message, or null when there is nothing worth showing.
 *
 * Capital `Message` is the house envelope (opening hours, menu hours, capacity,
 * sold out). Lowercase `message` is used by a few older handlers. `errors[]`
 * comes from express-validator. A transport failure has no body at all.
 */
export const serverMessage = (error) => {
  const data = error && error.response && error.response.data;
  if (!data) return null;

  if (typeof data.Message === 'string' && data.Message.trim()) return data.Message;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;

  // paramValidator answers 403 { Message: [{ param, msg }] }
  if (Array.isArray(data.Message) && data.Message.length) {
    const first = data.Message[0];
    if (first && typeof first.msg === 'string') return first.msg;
  }
  if (Array.isArray(data.errors) && data.errors.length) {
    const first = data.errors[0];
    if (first && typeof first.msg === 'string') return first.msg;
  }
  return null;
};

/*
 * What to show the guest: the server's explanation when there is one, otherwise
 * the caller's own fallback. Never axios's "Request failed with status code N",
 * which tells the guest nothing and looks like a crash.
 */
export const guestFacingError = (error, fallback) =>
  serverMessage(error) || fallback || 'There was an error processing your payment. Please try again.';

/*
 * The sold-out envelope: 409 with a `soldOut` array of
 * { _id, name, type, available, requested }. `available` is what is actually on
 * the shelf, so 0 means gone and anything higher means the basket asked for more
 * than exists — the backend already words the Message accordingly, in the
 * guest's language.
 */
export const soldOutFromError = (error) => {
  const data = error && error.response && error.response.data;
  if (!data || error.response.status !== 409) return null;
  if (!Array.isArray(data.soldOut) || data.soldOut.length === 0) return null;
  return data.soldOut;
};

/*
 * Ordering paused: the venue's heywaitr subscription is blocked. The backend
 * answers 403 { Message, Reason: 'subscription_blocked' }, Message already in
 * the guest's language. Kept apart from other 403s (paramValidator uses 403 too).
 */
export const subscriptionBlockedFromError = (error) =>
  Boolean(
    error &&
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.Reason === 'subscription_blocked',
  );

const serverError = {
  serverMessage,
  guestFacingError,
  soldOutFromError,
  subscriptionBlockedFromError,
};

export default serverError;
